import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import Button from '../../../components/buttons/Button';
import { useRef } from 'react';
import { getMedia, scrollToError } from '../../../utils/HelperFunctions';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { IWarranty } from '../types/interfaces';
import useFormsIntegrationHelpers from '../../../hooks/useFormsIntegrationHelpers';
import FormDynamicSkeleton from '../../../components/loaders/FormDynamicSkeleton';
import CreateWarrantyFormInputs from './CreateWarrantyFormInputs';

const CreateWarrantyForm = () => {
  const { id } = useParams();
  const formRef = useRef(null);
  const { t } = useTranslation();

  const { apiData, getDataLoading, handleSubmit, submitLoading } = useFormsIntegrationHelpers({
    queryKey: ['warranties', id],
    invalidateQueryKey: ['warranties'],
    id,
    singleGetApi: `/admin/warranties/${id}`,
    addApi: '/admin/warranties',
    editApi: `/admin/warranties/${id}`,
    listRoute: '/warranties',
    itemName: t('warranty')
  });

  const validationSchema = Yup.object({
    code: Yup.string().required(t('required')),
    title: Yup.object().shape({
      ar: Yup.string().required(t('required')).min(3, t('name_short')),
      en: Yup.string().required(t('required')).min(3, t('name_short'))
    }),
    description: Yup.object().shape({
      ar: Yup.string().required(t('required')).min(3, t('name_short')),
      en: Yup.string().required(t('required')).min(3, t('name_short'))
    }),
    vehicle_models: Yup.array().of(
      Yup.object().shape({
        vehicle_model_id: Yup.number().required(t('required')),
        price: Yup.number().required(t('required'))
      })
    )
  });

  const initialValues: IWarranty = {
    ...apiData,

    title: {
      en: apiData?.title?.en || '',
      ar: apiData?.title?.ar || ''
    },
    description: {
      en: apiData?.description?.en || '',
      ar: apiData?.description?.ar || ''
    },

    is_default: apiData?.is_default || 0,
    code: apiData?.code || '',

    vehicle_models:
      apiData?.vehicle_models?.map((item: any) => ({
        vehicle_model_id: item.id,
        price: item.warranty_price || 0
      })) || [],
    vehicle_make_ids: Array.from(new Set(apiData?.vehicle_models?.map((item: any) => item.vehicle_make?.id) )) || []
  };

  if (getDataLoading)
    return (
      <FormDynamicSkeleton
        sections={[
          [
            { columns: 2, items: 1 },
            { columns: 2, items: 2 },
            { columns: 2, items: 2, type: 'textarea' },
            { columns: 2, items: 1, type: 'switch' }
          ],
          [
            { columns: 2, items: 1 },
          ]
        ]}
        buttons={1}
      />
    );

  return (
    <div className={'form_section'} ref={formRef}>
      <Formik
        validateOnMount
        enableReinitialize
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={(values, { setErrors }) => {
          let value = {
            ...values,
            vehicle_models: values?.is_default ? [] : values?.vehicle_models,
            is_default: values?.is_default ? 1 : 0,
            _method: 'POST'
          };
          if (id) {
            value = { ...value, _method: 'PUT' };
          }
          handleSubmit({ ...value }, setErrors);
        }}
      >
        {(formik) => (
          <>
            <Form>
              <CreateWarrantyFormInputs />
              <div className="form_button reverse">
                <Button
                  loading={submitLoading}
                  onClick={() => {
                    scrollToError(!formik.isValid, formRef);
                  }}
                >
                  <span className="bold">{t('save')}</span>
                </Button>
              </div>
            </Form>
          </>
        )}
      </Formik>
    </div>
  );
};

export default CreateWarrantyForm;
